import * as React from 'react';
import { createContext, useContext, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { jsx, Fragment } from 'react/jsx-runtime';
import '@canvas-logic/clp-api-types';
import { k as createTranslator, g as LocalizationCapabilityError, L as LocalizationRequestError, o as layersFromProjectTranslations, j as createCatalogTranslator } from './shared/clientErrors-COds4E9J.mjs';

const DisplayContextReactContext = createContext({});
const DisplayContextProvider = DisplayContextReactContext.Provider;
function useDisplayContext() {
    return useContext(DisplayContextReactContext);
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const LocalizationReactContext = createContext(null);
function LocalizationProvider({ client, request, language, catalog, legacyFallback = true, children, }) {
    const [data, setData] = useState(null);
    const [status, setStatus] = useState('loading');
    const [error, setError] = useState(null);
    const [refreshCounter, setRefreshCounter] = useState(0);
    const requestSeq = useRef(0);
    const lastSnapshotId = useRef(undefined);
    const { productId, projectId, catalogId, draft, include, layers: requestLayers } = request;
    const includeKey = include && include.length > 0 ? include.join(',') : undefined;
    const layersKey = requestLayers && requestLayers.length > 0 ? requestLayers.join(',') : undefined;
    useEffect(() => {
        const seq = ++requestSeq.current;
        setStatus('loading');
        setError(null);
        const applyIfCurrent = (apply) => {
            if (requestSeq.current === seq)
                apply();
        };
        const load = () => __awaiter(this, void 0, void 0, function* () {
            try {
                const layered = yield client.getLayeredTranslations({
                    productId,
                    projectId,
                    catalogId,
                    draft,
                    lang: language,
                    include: includeKey === null || includeKey === void 0 ? void 0 : includeKey.split(','),
                    layers: layersKey === null || layersKey === void 0 ? void 0 : layersKey.split(','),
                    snapshotId: lastSnapshotId.current,
                });
                applyIfCurrent(() => {
                    lastSnapshotId.current = layered.snapshotId;
                    setData(toLoaded(layered));
                    setStatus('ready');
                });
            }
            catch (e) {
                const canFallBack = legacyFallback &&
                    (e instanceof LocalizationCapabilityError || (e instanceof LocalizationRequestError && e.status === 409));
                if (!canFallBack) {
                    applyIfCurrent(() => {
                        setError(e);
                        setStatus('error');
                    });
                    return;
                }
                try {
                    const loaded = yield loadLegacy(client, { productId, projectId, catalogId, draft }, language);
                    applyIfCurrent(() => {
                        lastSnapshotId.current = undefined;
                        setData(loaded);
                        setStatus('ready');
                    });
                }
                catch (legacyError) {
                    applyIfCurrent(() => {
                        setError(legacyError);
                        setStatus('error');
                    });
                }
            }
        });
        void load();
    }, [client, productId, projectId, catalogId, draft, language, legacyFallback, refreshCounter, includeKey, layersKey]);
    const refresh = useCallback(() => setRefreshCounter((c) => c + 1), []);
    const translator = useMemo(() => { var _a; return createTranslator({ layers: (_a = data === null || data === void 0 ? void 0 : data.layers) !== null && _a !== void 0 ? _a : {}, catalog }); }, [data === null || data === void 0 ? void 0 : data.layers, catalog]);
    const value = useMemo(() => {
        var _a, _b, _c, _d;
        return ({
            status,
            contract: (_a = data === null || data === void 0 ? void 0 : data.contract) !== null && _a !== void 0 ? _a : null,
            requestedLanguage: language,
            resolvedLanguage: (_b = data === null || data === void 0 ? void 0 : data.resolvedLanguage) !== null && _b !== void 0 ? _b : null,
            defaultLanguage: (_c = data === null || data === void 0 ? void 0 : data.defaultLanguage) !== null && _c !== void 0 ? _c : null,
            layers: (_d = data === null || data === void 0 ? void 0 : data.layers) !== null && _d !== void 0 ? _d : {},
            snapshotId: data === null || data === void 0 ? void 0 : data.snapshotId,
            translator,
            error,
            refresh,
        });
    }, [status, data, language, translator, error, refresh]);
    return jsx(LocalizationReactContext.Provider, { value: value, children: children });
}
function toLoaded(layered) {
    return {
        contract: 'v2',
        resolvedLanguage: layered.language,
        defaultLanguage: layered.defaultLanguage,
        layers: layered.layers,
        snapshotId: layered.snapshotId,
    };
}
function loadLegacy(client, request, language) {
    return __awaiter(this, void 0, void 0, function* () {
        const [primary, def] = yield Promise.all([
            client.getTranslations(Object.assign(Object.assign({}, request), { lang: language })),
            language != null ? client.getTranslations(Object.assign({}, request)).catch(() => null) : Promise.resolve(null),
        ]);
        const useFallback = def != null && def.language != null && def.language !== primary.language;
        const layers = layersFromProjectTranslations(useFallback ? Object.assign(Object.assign({}, primary), { fallback: def.translations }) : primary);
        return {
            contract: 'v1',
            resolvedLanguage: primary.language,
            defaultLanguage: useFallback ? def.language : primary.language,
            layers,
            snapshotId: undefined,
        };
    });
}
const SYSTEM_TRANSLATOR = createTranslator({ layers: {} });
const NO_PROVIDER_STATE = {
    status: 'ready',
    contract: null,
    requestedLanguage: undefined,
    resolvedLanguage: null,
    defaultLanguage: null,
    layers: {},
    snapshotId: undefined,
    translator: SYSTEM_TRANSLATOR,
    error: null,
    refresh: () => undefined,
};
function useLocalization() {
    var _a;
    return (_a = useContext(LocalizationReactContext)) !== null && _a !== void 0 ? _a : NO_PROVIDER_STATE;
}
function useTranslator() {
    return useLocalization().translator;
}
const LocalizationStateProvider = LocalizationReactContext.Provider;

function CatalogLocalizationProvider({ catalog, language, children, }) {
    const value = useMemo(() => {
        const translator = createCatalogTranslator(catalog, { language });
        return {
            status: 'ready',
            contract: null,
            requestedLanguage: language,
            resolvedLanguage: language !== null && language !== void 0 ? language : null,
            defaultLanguage: null,
            layers: {},
            snapshotId: undefined,
            translator,
            error: null,
            refresh: () => undefined,
        };
    }, [catalog, language]);
    return jsx(LocalizationStateProvider, { value: value, children: children });
}

function useUiLabel(key, ctxOverride) {
    const translator = useTranslator();
    const ambient = useDisplayContext();
    return translator.ui(key, ctxOverride !== null && ctxOverride !== void 0 ? ctxOverride : ambient);
}
function useEntityLabel(canonical, systemValue, ctxOverride) {
    const translator = useTranslator();
    const ambient = useDisplayContext();
    return translator.entity(canonical, ctxOverride !== null && ctxOverride !== void 0 ? ctxOverride : ambient, systemValue);
}
function useCustomMessage(key, vars) {
    const translator = useTranslator();
    return translator.custom(key, vars);
}

function ProjectTrans({ labelKey, ctx, components, values }) {
    const template = useUiLabel(labelKey, ctx);
    return jsx(Fragment, { children: interpolate(template, components !== null && components !== void 0 ? components : {}, values !== null && values !== void 0 ? values : {}) });
}
function interpolate(template, components, values) {
    const out = [];
    const re = /(<([a-zA-Z][\w-]*)\s*\/>)|(<([a-zA-Z][\w-]*)>([\s\S]*?)<\/\4>)|(\{\{(\w+)\}\})/g;
    let last = 0;
    let m;
    let key = 0;
    while ((m = re.exec(template)) != null) {
        if (m.index > last)
            out.push(template.slice(last, m.index));
        if (m[1]) {
            const tag = m[2];
            const c = components[tag];
            if (c)
                out.push(React.cloneElement(c, { key: key++ }));
        }
        else if (m[3]) {
            const tag = m[4];
            const inner = m[5];
            const c = components[tag];
            if (c)
                out.push(React.cloneElement(c, { key: key++ }, inner));
            else
                out.push(inner);
        }
        else if (m[6]) {
            const v = values[m[7]];
            out.push(v != null ? String(v) : `{{${m[7]}}}`);
        }
        last = re.lastIndex;
    }
    if (last < template.length)
        out.push(template.slice(last));
    return out;
}

export { CatalogLocalizationProvider, DisplayContextProvider, LocalizationProvider, LocalizationStateProvider, ProjectTrans, useCustomMessage, useDisplayContext, useEntityLabel, useLocalization, useTranslator, useUiLabel };
//# sourceMappingURL=react.mjs.map
